import { useEffect } from 'react'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { useNavigate } from 'react-router'

import { getCurrentUser } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import {
  Loader,
  Text,
  Button,
  ListCustomBullets,
  Card,
  CardColors,
  Image,
} from '@/ui'
import { useAnalyticsView } from '@/features/amplitude'
import { TypographyStyles, TextColors } from '@/styles'
import { selectPlaidItems } from '@/selectors/financeSelectors'
import { fetchPlaidItemsIfNeeded } from '@/actions/plaidItemActions'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useSetupLandingAnimation } from './useSetupLandingAnimation'

const BOOKS_COMPLETE_ICON_URL =
  'https://heard-images.s3.amazonaws.com/assets/Books%20complete-no%20confetti.svg'

export const SetupLandingCard = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const currentUser = useReselector(getCurrentUser)
  const pageView = useAnalyticsView()
  const { headingRef, subtitleRef, ctaRef, cardRef } =
    useSetupLandingAnimation()

  const plaidItems = useReselector(selectPlaidItems)

  const nextOnboardingPath = '/onboarding-v2?stage=banking-intro'

  useEffect(() => {
    dispatch(fetchPlaidItemsIfNeeded())
  }, [dispatch])

  useEffect(() => {
    pageView('onboarding items landing')
  }, [pageView])

  if (!currentUser) return <Loader active />

  return (
    <div className="flex justify-center flex-col items-center">
      <div ref={headingRef}>
        <Text variant={TypographyStyles.DISPLAY_MD} textAlign="center">
          Let&apos;s get your books organized, {currentUser.firstName}
        </Text>
      </div>
      <div ref={subtitleRef}>
        <Text variant={TypographyStyles.BODY_LG} textAlign="center">
          Set up your Heard account in a few easy steps
        </Text>
      </div>

      <div ref={ctaRef} className="flex flex-col items-center">
        <Button
          onClick={() => navigate(nextOnboardingPath)}
          iconRight={faArrowRight}
          className="mt-6 max-w-75"
        >
          Get started
        </Button>

        {Boolean(Object.values(plaidItems).length) && (
          <Button
            variant="ghost"
            className="mt-2"
            onClick={() => navigate(nextOnboardingPath)}
          >
            View Connected Accounts
          </Button>
        )}

        <Text
          variant={TypographyStyles.BODY_XS}
          textAlign="center"
          className="mt-2 max-w-75"
        >
          We use Plaid&apos;s secure technology to connect to your bank. We
          never see your login credentials.
        </Text>
      </div>

      <div className="mt-14 flex justify-center w-full">
        <Card
          color={CardColors.GREEN_GRADIENT}
          className="my-0 max-w-[450px]"
          ref={cardRef}
        >
          <div className="flex gap-4 items-start w-full">
            <div className="flex-1 min-w-0">
              <Text variant={TypographyStyles.H1}>How it works</Text>
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                Get clean, tax-ready financials with every deduction captured.
              </Text>
            </div>
            <Image
              src={BOOKS_COMPLETE_ICON_URL}
              alt=""
              className="shrink-0 size-20"
              objectFit="contain"
            />
          </div>
          <ListCustomBullets variant="number" className="mt-4 max-w-100">
            <ListCustomBullets.Item
              numberBgColor="bg-moss-50"
              numberTextColor="text-moss-300"
              heading="Connect your bank"
              description="Securely link your accounts, and we'll pull in transactions automatically."
            />
            <ListCustomBullets.Item
              numberBgColor="bg-moss-50"
              numberTextColor="text-moss-300"
              heading="Flag personal transactions"
              description="We'll help you quickly sort personal spending out of your business books."
            />
            <ListCustomBullets.Item
              numberBgColor="bg-moss-50"
              numberTextColor="text-moss-300"
              heading="We review everything"
              description="Our bookkeeping team checks your transactions for accuracy and flags anything that needs your input."
            />
          </ListCustomBullets>
        </Card>
      </div>
    </div>
  )
}
